import * as React from "react";
import { Header, SpaceBetween, Spinner, TextContent, Box, Button, Flashbar, FlashbarProps, Icon } from "@amzn/awsui-components-react";
import { Builder } from "builder-pattern";
import { useAPI, useAPI2 } from "../../hooks/api-hook";
import {
    EntityFactory,
    EntityPermissionOperationNames,
    EntityRef,
    EntityType,
    IEntityPermission,
    IMetrics,
    IPageDraft,
    InstanceFactory,
    PageDraft,
    PageLibraryFactory,
    APIOutput
} from "@amzn/ask-legal-domain";
import { PageSection } from "../../components/page-section/PageSectionComp";
import { PageBaseLayout } from "../../components/page/PageBaseLayout";
import { AppContext } from "../../setup/context";
import { Preference } from "../../setup/preference";
import { usePageTitle } from "../../contexts/TitleContext";
import { UIModel } from "../../model/ui-model";
import { FavorButton } from "../../components/common/FavorButton";
import { PageDeprecated } from "../redirects/PageDeprecated";
import { LegalOnlyBadge } from "../../components/common/LegalOnlyTag";
import { PermissionDenied } from "../redirects/PermissionDenied";
import { PageNotFound } from "../redirects/PageNotFound";
import { CaptureAnalyticsComp } from "../../components/analytics/CaptureAnalyticsComp";
import { DateTimeFormatter } from "../../utils/date-time-utils";
import { PageContentEvaluation } from "../../components/page/PageContentEvaluation";
import { PageFeedbackModal } from "../../components/page/modal-content/PageFeedbackModal";
import { StarRating } from "../../components/common/StarRating";
import { ErrorFlashbar } from "../../components/common/ErrorFlashbar";
import { DraftConflictModal } from "../../components/common/DraftConflictModal";
import { FeedbackRating, IPageFeedback } from "@amzn/ask-legal-domain";
import "../../styles/component/layout/AskLegalLayout.scss";

export const PageLive = (props: {
    id: string;
    version?: number
}) => {
    const context = React.useContext(AppContext);
    const loadPage2Runner = useAPI(
        context.getPageAPI().load2
    );
    const loadInstanceRunner = useAPI2(
        context.getInstanceAPI().load
    );
    const loadPageHistoryRunner = useAPI(
        context.getPageAPI().loadVersion
    );
    const recordViewHistoryRunner = useAPI(
        context.getCommonAPI().recordViewHistory
    );
    const createFromVersionRunner = useAPI(
        context.getPageDraftAPI().createFromVersion
    );
    const isFavoritedState = UIModel.State.use<boolean>({
        initialValue: false
    });
    const isAdminRunner = useAPI2(context.getEntityPermissionAPI().isAuthorized);
    const isEditorRunner = useAPI2(context.getEntityPermissionAPI().isAuthorized);
    const isPageEditorRunner = useAPI2(context.getEntityPermissionAPI().isAuthorized);

    const [hasEditorPermission, setHasEditorPermission] = React.useState(false);
    const [hasAdminPermission, setHasAdminPermission] = React.useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = React.useState(false);
    const [pageRating, setPageRating] = React.useState<FeedbackRating | null>(null);

    const submitFeedbackRunner = useAPI(context.getPageFeedbackAPI().submit);
    const loadFeedbackRunner = useAPI(context.getPageFeedbackAPI().load);

    // History-view hooks — must be declared before any conditional return (Rules of Hooks)
    const createInitiated = React.useRef(false);
    const discardInitiated = React.useRef(false);
    const [showConflictModal, setShowConflictModal] = React.useState(false);
    const discardRunner = useAPI(
        context.getPageDraftAPI().discard
    );

    const handleRatingChange = async (newRating: FeedbackRating) => {
        setPageRating(newRating);

        if (loadInstanceRunner.output && loadPage2Runner.status === "Succeeded") {
            const input = IPageFeedback.SubmitFeedbackInput.create({
                pageRef: EntityFactory.toEntityRef(loadPage2Runner.data.output.page),
                rating: newRating,
                sessionId: `session-${Date.now()}`,
                url: window.location.href
            });
            setShowFeedbackModal(true);
        }
    };

    React.useEffect(() => {
        isAdminRunner.invoke(IEntityPermission.IsAuthorizedInput.create(
            InstanceFactory.fromEntityId(props.id),
            EntityType.Instance,
            EntityPermissionOperationNames.CAN_ADMIN
        ));
    }, []);

    React.useEffect(() => {
        if (isAdminRunner.status === "Succeeded") {
            if (isAdminRunner.output) {
                setHasAdminPermission(true);
                setHasEditorPermission(true);
            } else {
                isEditorRunner.invoke(IEntityPermission.IsAuthorizedInput.create(
                    PageLibraryFactory.fromEntityId(props.id),
                    EntityType.PageLibrary,
                    EntityPermissionOperationNames.CAN_EDIT
                ));
            }
        } else if (isAdminRunner.status === "Error") {
            console.error("Failed to check admin permission, falling back to editor check");
            isEditorRunner.invoke(IEntityPermission.IsAuthorizedInput.create(
                PageLibraryFactory.fromEntityId(props.id),
                EntityType.PageLibrary,
                EntityPermissionOperationNames.CAN_EDIT
            ));
        }
    }, [isAdminRunner.status]);

    React.useEffect(() => {
        if (isEditorRunner.status === "Succeeded" && isEditorRunner.output) {
            setHasEditorPermission(true);
        } else if (isEditorRunner.status === "Succeeded" && !isEditorRunner.output) {
            isPageEditorRunner.invoke(IEntityPermission.IsAuthorizedInput.create(
                props.id,
                EntityType.Page,
                EntityPermissionOperationNames.CAN_EDIT
            ));
        } else if (isEditorRunner.status === "Error") {
            console.error("Failed to check PL editor permission, falling back to page editor check");
            isPageEditorRunner.invoke(IEntityPermission.IsAuthorizedInput.create(
                props.id,
                EntityType.Page,
                EntityPermissionOperationNames.CAN_EDIT
            ));
        }
    }, [isEditorRunner.status]);

    React.useEffect(() => {
        if (isPageEditorRunner.status === "Succeeded" && isPageEditorRunner.output) {
            setHasEditorPermission(true);
        } else if (isPageEditorRunner.status === "Error") {
            console.error("Failed to check page editor permission");
        }
    }, [isPageEditorRunner.status]);

    const pageTitle = React.useMemo(() => {
        if (!props.version && loadPage2Runner.status === "Succeeded") {
            return { title: loadPage2Runner.data.output.page.title, withSuffix: false };
        }
        if (props.version && loadPageHistoryRunner.status === "Succeeded") {
            return {
                title: `${loadPageHistoryRunner.data.output.title} (Version ${props.version})`,
                withSuffix: true
            };
        }
        return { title: undefined, withSuffix: false };
    }, [props.version, loadPage2Runner.status, loadPage2Runner.data, loadPageHistoryRunner.status, loadPageHistoryRunner.data]);

    usePageTitle(pageTitle.title, pageTitle.withSuffix);

    React.useEffect(() => {
        if (loadPage2Runner.status === "Succeeded") {
            isFavoritedState.setValue(loadPage2Runner.data.output.isFavorited);
            recordViewHistoryRunner.submitRun(
                IMetrics.RecordViewHistoryInput.create({
                    url: `/page/${props.id}/live`,
                    entityId: props.id,
                    type: "LivePage"
                })
            );
            loadExistingFeedback();
        }
    }, [loadPage2Runner.status]);

    const loadExistingFeedback = async () => {
        if (loadPage2Runner.status === "Succeeded") {
            try {
                const identity = await context.getIdentity();
                const input = IPageFeedback.LoadFeedbackInput.create({
                    pageRef: EntityFactory.toEntityRef(loadPage2Runner.data.output.page),
                    userId: identity.id // Load feedback for current user only
                });

                await loadFeedbackRunner.submitRun(input);
            } catch (error) {
                console.error("Failed to load existing feedback:", error);
            }
        }
    };

    React.useEffect(() => {
        if (loadFeedbackRunner.status === "Succeeded" && loadFeedbackRunner.data.output.feedbacks.length > 0) {
            const userFeedback = loadFeedbackRunner.data.output.feedbacks[0];
            setPageRating(userFeedback.rating);
        }
    }, [loadFeedbackRunner.status]);

    React.useEffect(() => {
        // Always load current page data (needed for draft ID construction in discard flow)
        loadPage2Runner.submitRun(props.id);
        if (props.version != null) {
            loadPageHistoryRunner.submitRun(
                EntityFactory.fromEntityAttributes(
                    props.id,
                    EntityType.Page,
                    props.version
                )
            );
        }
        loadInstanceRunner.invoke(InstanceFactory.fromEntityId(props.id));
    }, [props.id, props.version]);

    // Redirect to draft editor after successful createFromVersion
    React.useEffect(() => {
        if (!createInitiated.current) return;
        if (createFromVersionRunner.status === "Succeeded") {
            createInitiated.current = false;
            window.location.assign(`/#/page/${props.id}/admin?tab=draftPage`);
        } else if (createFromVersionRunner.status === "Error") {
            if (createFromVersionRunner.data.err?.code === 409) {
                setShowConflictModal(true);
            }
            createInitiated.current = false;
        }
    }, [createFromVersionRunner.status, createFromVersionRunner.data, props.id]);

    // After successful discard, create new draft from the selected version
    React.useEffect(() => {
        if (!discardInitiated.current) return;
        if (discardRunner.status === "Succeeded") {
            discardInitiated.current = false;
            if (props.version == null) return;
            setShowConflictModal(false);
            createInitiated.current = true;
            createFromVersionRunner.submitRun(
                IPageDraft.CreateFromVersionInput.create({
                    pageId: props.id,
                    sourceVersion: props.version
                })
            );
        } else if (discardRunner.status === "Error") {
            // Keep discardInitiated true so the error displays in the modal.
            // It gets reset when the user closes the modal.
        }
    }, [discardRunner.status, discardRunner.data, props.id, props.version]);

    if (!props.version) {
        // Render live page
        return (
            <React.Fragment>
                <React.Fragment>
                    {loadPage2Runner.status === "Succeeded" && !loadPage2Runner.data.output.page.deprecated && (
                        <div className="sherpa-page-header">
                            <SpaceBetween direction="vertical" size="xxxs">
                                <div className="sherpa-page-title">
                                    <h2>{loadPage2Runner.data.output.page.title}</h2>
                                    <FavorButton
                                        size="big"
                                        isFavoritedState={isFavoritedState}
                                        targetEntityRef={EntityFactory.toEntityRef(loadPage2Runner.data.output.page)}
                                    />
                                    <LegalOnlyBadge pageLibraryId={PageLibraryFactory.fromEntityId(props.id)}/>
                                </div>
                                <TextContent className="sherpa-page-title-date">
                                    <small>
                                        &nbsp;&nbsp;&nbsp;&nbsp;Updated on {
                                            DateTimeFormatter.toSimpleDate(
                                                loadPage2Runner.data.output.page.freshnessDate ?
                                                    loadPage2Runner.data.output.page.freshnessDate :
                                                    loadPage2Runner.data.output.page.lastModifiedDate,
                                                1
                                            )
                                        }
                                        {!!loadInstanceRunner.output?.preferences?.features?.page?.showLastModifiedBy &&
                                            (
                                                <>
                                                    &nbsp;by <a
                                                        style={{ textDecoration: "None" }}
                                                        href={`https://phonetool.amazon.com/users/${loadPage2Runner.data.output.page.lastModifiedBy.id}`}
                                                        target="_blank"
                                                    >
                                                        {loadPage2Runner.data.output.page.lastModifiedBy.name}
                                                    </a>
                                                </>
                                            )}
                                    </small>
                                </TextContent>
                                {context.hasLab(Preference.Lab.PageFeedback) &&
                                    <Box margin={{ top: "xs" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px", position: "relative" }}>
                                            <span style={{ fontSize: "14px", fontWeight: "bold" }}>
                                                How useful was this page:
                                            </span>
                                            <StarRating
                                                value={pageRating}
                                                onChange={handleRatingChange}
                                                size="medium"
                                            />
                                            {loadPage2Runner.status === "Succeeded" && loadInstanceRunner.output && (
                                                <PageFeedbackModal
                                                    pageId={props.id}
                                                    instanceId={loadInstanceRunner.output.id}
                                                    visible={showFeedbackModal}
                                                    onDismiss={() => setShowFeedbackModal(false)}
                                                    pageRef={EntityFactory.toEntityRef(loadPage2Runner.data.output.page)}
                                                    selectedRating={pageRating}
                                                />
                                            )}
                                        </div>
                                    </Box>
                                }
                                <Box margin={{ top: "s" }}>
                                    <PageContentEvaluation
                                        pageRef={{
                                            id: props.id,
                                            version: props.version || 0,
                                            type: EntityType.Page
                                        }}
                                    />
                                </Box>
                            </SpaceBetween>
                        </div>
                    )}
                    {loadPage2Runner.status === "Running" &&
                        <span
                            className="awsui-util-status-inactive vertical-center horizontal-center"
                            style={{ paddingTop: "25px" }}
                        >
                            <Spinner size="large"/> Locating Page Resources...
                        </span>
                    }
                </React.Fragment>
                <React.Fragment>
                    {loadPage2Runner.status === "Error" && (
                        loadPage2Runner.data.err.code === 403 ?
                            <PermissionDenied entityId={props.id} accessType="LivePage"/> : <PageNotFound/>
                    )}
                    {loadPage2Runner.status === "Succeeded" && <>
                        {loadPage2Runner.data.output.page.deprecated ?
                            <PageDeprecated
                                pageOwner={loadPage2Runner.data.output.page.pageOwner}
                                forwardingUrl={loadPage2Runner.data.output.page.forwardingUrl}
                            /> :
                            <CaptureAnalyticsComp
                                entityRef={EntityFactory.toEntityRef(loadPage2Runner.data.output.page)}
                            >
                                <PageBaseLayout expandable
                                    mainContent={(() => {
                                        const section = loadPage2Runner.data.output.page.sections.find(s => s.location === "Main");
                                        return section ? <PageSection.View section={section} editor={hasEditorPermission} /> : <></>;
                                    })()}
                                    rightContent={(() => {
                                        const section = loadPage2Runner.data.output.page.sections.find(s => s.location === "Right");
                                        return section ? <PageSection.View section={section} editor={hasEditorPermission} /> : <></>;
                                    })()}
                                    hideRightSection={(() => {
                                        const rightSection = loadPage2Runner.data.output.page.sections.find(s => s.location === "Right");
                                        return !!rightSection && rightSection.containers.length === 0;
                                    })()}
                                />
                            </CaptureAnalyticsComp>
                        }
                    </>}
                </React.Fragment>
            </React.Fragment>
        );
    }
    // Render history page for specific version

    return (
        <React.Fragment>
            <React.Fragment>
                {loadPageHistoryRunner.status === "Succeeded" ? (
                    <SpaceBetween direction="horizontal" size="s">
                        <Header variant="h2">{loadPageHistoryRunner.data.output.title} (Version {props.version})</Header>
                        {hasEditorPermission && props.version != null && (
                            <Button
                                variant="primary"
                                loading={createFromVersionRunner.status === "Running"}
                                onClick={() => {
                                    if (props.version == null) return;
                                    createInitiated.current = true;
                                    createFromVersionRunner.submitRun(
                                        IPageDraft.CreateFromVersionInput.create({
                                            pageId: props.id,
                                            sourceVersion: props.version
                                        })
                                    );
                                }}>
                                Create Draft from this Version
                            </Button>
                        )}
                    </SpaceBetween>
                ) : (
                    <Header variant="h2">{props.id.split("-")[2]}</Header>
                )}
            </React.Fragment>
            {createFromVersionRunner.status === "Error" && createFromVersionRunner.data.err?.code !== 409 && (
                <ErrorFlashbar error={createFromVersionRunner.data.err}/>
            )}
            <React.Fragment>
                {loadPageHistoryRunner.status === "Running" && <Spinner />}
                {loadPageHistoryRunner.status === "Error" && (
                    loadPageHistoryRunner.data.err.code === 403 ?
                        <PermissionDenied entityId={props.id} accessType="LivePage"/> : <PageNotFound/>
                )}
                {loadPageHistoryRunner.status === "Succeeded" &&
                    <PageBaseLayout expandable
                        mainContent={(() => {
                            const section = loadPageHistoryRunner.data.output.sections.find(s => s.location === "Main");
                            return section ? <PageSection.View section={section} /> : <></>;
                        })()}
                        rightContent={(() => {
                            const section = loadPageHistoryRunner.data.output.sections.find(s => s.location === "Right");
                            return section ? <PageSection.View section={section} /> : <></>;
                        })()}
                    />
                }
            </React.Fragment>
            {/* Conflict modal: draft already exists for this page */}
            {showConflictModal && (
                <DraftConflictModal
                    onContinueEditing={() => {
                        setShowConflictModal(false);
                        window.location.assign(`/#/page/${props.id}/admin?tab=draftPage`);
                    }}
                    onDiscardAndCreate={() => {
                        const currentVersion = loadPage2Runner.data?.output?.page?.version;
                        if (currentVersion == null) return;
                        const draftId = PageDraft.Factory.toPageDraftId(
                            Builder<EntityRef>().id(props.id).version(currentVersion).build()
                        );
                        discardInitiated.current = true;
                        discardRunner.submitRun(draftId);
                    }}
                    isDiscarding={discardRunner.status === "Running"}
                    discardError={
                        discardInitiated.current && discardRunner.status === "Error"
                            ? discardRunner.data.err
                            : undefined
                    }
                    onCancel={() => {
                        discardInitiated.current = false;
                        setShowConflictModal(false);
                    }}
                />
            )}
        </React.Fragment>
    );
};
